import { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import RecipeList from "../Recipes/RecipeList";
import supabase from "../supabaseClient";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ShoppingList } from "./ShoppingList";
import { CustomMarkdown } from "./CustomMarkdown";
import { useTheme } from "@mui/material/styles";
import UserContext from "../contexts/userContext";

function FavoriteRecipes({ listId, listName }) {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [shoppingListLoading, setShoppingListLoading] = useState(false);
  const [markdownContent, setMarkdownContent] = useState("");
  const [isListExist, setIsListExist] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const esRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchFavoriteRecipes = async () => {
      try {
        const { data: recipesData, error: recipesError } = await supabase
          .from("lists_recipes")
          .select(
            `
            list_recipe_id,
            recipes (
              *,
              books ( name, author )
            )
          `
          )
          .eq("list_id", listId);

        if (recipesError || !recipesData) {
          throw new Error("Could not fetch favorite recipes.");
        }

        // Formatting the data as required
        const formattedData = recipesData.map((item) => ({
          list_recipe_id: item.list_recipe_id,
          id: item.recipes.id,
          title: item.recipes.title,
          description: item.recipes.description,
          ingredients: item.recipes.ingredients,
          instructions: item.recipes.instructions,
          yield: item.recipes.yield,
          time: item.recipes.time,
          inactive_time: item.recipes.inactive_time,
          path: item.recipes.path,
          image_url: item.recipes.image_url,
          books: item.recipes.books ? item.recipes.books : null,
        }));

        setFavoriteRecipes(formattedData);
        setLoading(false);
      } catch (error) {
        setErrorMessage(error.message);
        setLoading(false);
      }
    };

    fetchFavoriteRecipes();

    // Supabase Realtime Subscription for lists_recipes
    const listsRecipesSubscription = supabase
      .channel(`public:lists_recipes_list_id=eq.${listId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "lists_recipes",
          filter: `list_id=eq.${listId}`,
        },
        (payload) => {
          console.log("Realtime update on lists_recipes:", payload);
          fetchFavoriteRecipes();
        }
      )
      .subscribe();

    // Clean up subscription on unmount
    return () => {
      supabase.removeChannel(listsRecipesSubscription);
    };
  }, [user, listId]);

  useEffect(() => {
    const fetchShoppingList = async () => {
      try {
        const { data: listData, error: listError } = await supabase
          .from("lists_shopping_list")
          .select("ingredient_json, markdown_content")
          .eq("list_id", listId);

        if (listError) {
          throw new Error("Could not fetch shopping list content.");
        }

        if (listData && listData.length > 0) {
          setIsListExist(true);
          setMarkdownContent(listData[0].markdown_content || "");
        } else {
          setIsListExist(false);
          setMarkdownContent("");
        }
      } catch (error) {
        console.error("Error fetching shopping list content:", error);
      }
    };

    if (listId) {
      fetchShoppingList();

      // Supabase Realtime Subscription for lists_shopping_list
      const listsShoppingListSubscription = supabase
        .channel(`public:lists_shopping_list_list_id=eq.${listId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "lists_shopping_list",
            filter: `list_id=eq.${listId}`,
          },
          (payload) => {
            console.log("Realtime update on lists_shopping_list:", payload);
            if (payload.eventType === "DELETE") {
              setIsListExist(false);
              setMarkdownContent("");
            } else if (payload.new) {
              setIsListExist(true);
              setMarkdownContent(payload.new.markdown_content || "");
            }
          }
        )
        .subscribe();

      // Clean up subscription on unmount
      return () => {
        supabase.removeChannel(listsShoppingListSubscription);
      };
    }
  }, [listId]);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const generateShoppingList = async () => {
    setMarkdownContent("");
    setShoppingListLoading(true);

    const allIngredients = favoriteRecipes.flatMap((recipe) =>
      recipe.ingredients.map((ing) => ({
        name: ing.name,
        quantity: ing.quantity,
      }))
    );

    try {
      // Update markdown_content column to empty string for the given listId
      const { error: updateError } = await supabase
        .from("lists_shopping_list")
        .update({ markdown_content: "" })
        .eq("list_id", listId);

      if (updateError) {
        throw new Error("Could not update markdown_content.");
      }

      setIsGenerating(true);

      const response = await fetch(`${SERVER_URL}/api/lists/create-list`, {
        method: "POST",
        body: JSON.stringify({ ingredients: allIngredients, list_id: listId }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const { requestId } = await response.json();

      // Start an SSE connection using the requestId
      esRef.current = new EventSource(
        `${SERVER_URL}/api/lists/create-list/${requestId}`
      );

      // Handle incoming messages (updates) from the server
      esRef.current.onmessage = async (event) => {
        const content = JSON.parse(event.data);
        setMarkdownContent((prevContent) => {
          const updatedContent = prevContent + content;

          // Update markdown_content in the database
          try {
            supabase
              .from("lists_shopping_list")
              .update({ markdown_content: updatedContent })
              .eq("list_id", listId);
          } catch (error) {
            console.error("Error updating shopping list content:", error);
          }

          return updatedContent;
        });
      };

      // Handle errors
      esRef.current.onerror = () => {
        esRef.current.close();
        setIsListExist(true);
        setShoppingListLoading(false);
        setIsGenerating(false);
      };
    } catch (error) {
      console.error("Failed to send ingredients:", error);
      setShoppingListLoading(false);
      setIsGenerating(false);
    }
  };

  // Clean up EventSource when component unmounts
  useEffect(() => {
    return () => {
      if (esRef.current) {
        esRef.current.close();
      }
    };
  }, []);

  return (
    <Grid>
      <Box sx={{ marginTop: theme.spacing(2) }}>
        <Typography
          variant="h5"
          sx={{ fontSize: "1.3rem", fontFamily: theme.typography.fontFamily }}
        >
          {listName}
        </Typography>
      </Box>
      <Box>
        {loading ? (
          <Typography>loading...</Typography>
        ) : errorMessage ? (
          <Typography color="error">{errorMessage}</Typography>
        ) : favoriteRecipes.length === 0 ? (
          <Typography>no recipes saved.</Typography>
        ) : (
          <RecipeList
            loading={loading}
            recipes={favoriteRecipes}
            showResultsText={true}
            totalCount={favoriteRecipes.length}
            isFavorited={true}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: theme.spacing(2),
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            color={isListExist ? "secondary" : "primary"}
            onClick={generateShoppingList}
            disabled={shoppingListLoading || isListExist || isGenerating}
            sx={{ fontFamily: theme.typography.fontFamily, mr: 1 }}
          >
            {isGenerating ? "generating..." : "generate shopping list"}
          </Button>
          {shoppingListLoading ? (
            <CircularProgress size={24} />
          ) : (
            isListExist && (
              <RefreshIcon
                sx={{ cursor: "pointer" }}
                onClick={generateShoppingList}
              />
            )
          )}
        </Box>
        <Box>
          {shoppingListLoading && markdownContent ? (
            <CustomMarkdown
              content={markdownContent}
              setMarkdownContent={setMarkdownContent}
              disabled={shoppingListLoading}
            />
          ) : isListExist ? (
            <ShoppingList listId={listId} />
          ) : null}
        </Box>
      </Box>
    </Grid>
  );
}

export default FavoriteRecipes;